import React, { useMemo, useState } from 'react';
import { format } from 'date-fns-tz';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { convertToTimezone } from 'utils/dateTimeUtils';
import { cn } from '@epoch-ui/lib/utils';
import { Icon } from '@epoch-ui/components/Icon';
import { Badge } from '@epoch-ui/components/Badge';
import { GuestStatus } from 'types/guest.types';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';
import { CommunicationPreviewModal } from '@event-editor/components/CommunicationPreview/CommunicationPreviewModal';
import { getDisplayTextForStatus } from 'components/dashboard/EventInsights/EventInsightsGuestList/GuestSideSheet/utils';
import {
  useEditSelfCheckinModal,
  useEditFeedbackSettingsModal,
  useResendFeedbackFormModal
} from '@event-editor/hooks/eventEditorState';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { resendFeedbackRequestApi } from '@event-editor/api/feedback/resendFeedbackRequest';

import {
  EPOCH_COMMUNICATION_NOTIFICATION,
  EPOCH_COMMUNICATION_STATUS,
  EPOCH_COMMUNICATION_TYPE,
  COMMUNICATION_REMINDER_MEDIUM
} from '../../types/communication.types';

import { NotificationCardDropdown } from './NotificationCardDropdown';

// Icon mapping for notification types
const NOTIFICATION_TYPE_ICONS = {
  [EPOCH_COMMUNICATION_TYPE.FEEDBACK]: 'smile',
  [EPOCH_COMMUNICATION_TYPE.REMINDER]: 'bell',
  [EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK]: 'smile',
  [EPOCH_COMMUNICATION_TYPE.EVENT_QUESTIONNAIRE]: 'message-circle-question',
  [EPOCH_COMMUNICATION_TYPE.SELF_CHECK_IN]: 'circle-check'
} as const;

const DEFAULT_NOTIFICATION_ICON = 'bell';

// Badge mapping for notification statuses
const NOTIFICATION_STATUS_BADGES = {
  [EPOCH_COMMUNICATION_STATUS.SENT]: { variant: 'positive', text: 'Sent' },
  [EPOCH_COMMUNICATION_STATUS.SCHEDULED]: { variant: 'secondary', text: 'Scheduled' },
  [EPOCH_COMMUNICATION_STATUS.FAILED]: { variant: 'destructive', text: 'Failed' },
  [EPOCH_COMMUNICATION_STATUS.RECURRING]: { variant: 'secondary', text: 'Recurring' }
} as const;

const DEFAULT_BADGE = { variant: 'secondary', text: 'Unknown' } as const;

interface NotificationCardProps {
  epochCommunication: EPOCH_COMMUNICATION_NOTIFICATION;
  disabled?: boolean;
}

export const NotificationCard: React.FC<NotificationCardProps> = ({
  epochCommunication,
  disabled = false
}) => {
  const { title, type, status, targetStatuses, sendAt, mediums } = epochCommunication;
  const { id: eventId, timezoneCode } = useEditableEvent();
  const queryClient = useQueryClient();
  const [showPreview, setShowPreview] = useState(false);

  const selfCheckinModal = useEditSelfCheckinModal();
  const editFeedbackSettingsModal = useEditFeedbackSettingsModal();
  const resendFeedbackFormModal = useResendFeedbackFormModal();
  const { showToast } = useToaster();

  const deleteMutation = useMutation({
    mutationFn: resendFeedbackRequestApi.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: eventEditorQueryKeys.eventCommunication(eventId!)
      });
    },
    onError: (error) => {
      console.error(error);
      showToast('Failed to delete resend feedback request', { variant: ToasterVariant.ERROR });
    }
  });

  // Get badge configuration for status
  const badgeConfig = NOTIFICATION_STATUS_BADGES[status] || DEFAULT_BADGE;

  /**
   * Get correct notification description for each notification
   * eg; "To: Going, Incomplete • 5:00 AM PDT • Email & Slack"
   * "Daily • Email & Slack"
   */
  const notificationDescription = useMemo(() => {
    const statusText =
      targetStatuses.length > 0
        ? `To: ${targetStatuses
            .map((targetStatus) => getDisplayTextForStatus(targetStatus as GuestStatus))
            .join(', ')}`
        : '';

    const tzCode = timezoneCode || 'UTC';
    const timeText =
      status === EPOCH_COMMUNICATION_STATUS.RECURRING
        ? `Daily`
        : format(convertToTimezone(sendAt, tzCode), 'MMM d, yyyy, h:mm aa z', {
            timeZone: tzCode
          });

    // Format mediums
    const mediumText = mediums
      .map((medium) => {
        switch (medium) {
          case COMMUNICATION_REMINDER_MEDIUM.EMAIL:
            return 'Email';
          case COMMUNICATION_REMINDER_MEDIUM.SLACK:
            return 'Slack';
          default:
            return medium;
        }
      })
      .join(' & ');

    return `${statusText ? `${statusText} • ` : ''}${timeText} • ${mediumText}`;
  }, [targetStatuses, sendAt, mediums, status, timezoneCode]);

  // Action handlers
  const handlePreview = (): void => {
    setShowPreview(true);
  };

  const handleEdit = (): void => {
    switch (type) {
      case EPOCH_COMMUNICATION_TYPE.SELF_CHECK_IN:
        selfCheckinModal.open();
        break;
      case EPOCH_COMMUNICATION_TYPE.FEEDBACK:
        editFeedbackSettingsModal.open();
        break;
      case EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK:
        resendFeedbackFormModal.open({ feedbackRequest: epochCommunication });
        break;
      default:
        break;
    }
  };

  const handleDelete = (): void => {
    if (type === EPOCH_COMMUNICATION_TYPE.RESEND_FEEDBACK) {
      deleteMutation.mutate(epochCommunication.id);
    }
  };

  const previewOnly = status !== EPOCH_COMMUNICATION_STATUS.SCHEDULED;

  return (
    <div className={cn('tw-flex tw-items-center tw-justify-between ', disabled && 'tw-opacity-40')}>
      <div className="tw-flex tw-items-center tw-gap-2">
        <Icon
          name={NOTIFICATION_TYPE_ICONS[type] || DEFAULT_NOTIFICATION_ICON}
          size={20}
          className="tw-text-medium-grey"
        />

        <button
          type="button"
          disabled={disabled}
          className="tw-ml-4 tw-p-0 tw-text-left"
          onClick={() => (previewOnly ? handlePreview() : handleEdit())}
        >
          <p className="tw-m-0 tw-font-medium tw-leading-4">{title}</p>
          <p className="tw-m-0 tw-text-xs tw-font-normal tw-text-primary-grey">
            {notificationDescription}
          </p>
        </button>
      </div>

      <div className="tw-flex tw-items-center tw-gap-2">
        <Badge variant={badgeConfig.variant}>{badgeConfig.text}</Badge>
        <NotificationCardDropdown
          epochCommunication={epochCommunication}
          onEdit={handleEdit}
          onPreview={handlePreview}
          onDelete={handleDelete}
        />
      </div>

      <CommunicationPreviewModal
        open={showPreview}
        onOpenChange={setShowPreview}
        communication={epochCommunication}
      />
    </div>
  );
};
